/**
 * The player universe cache (§4.1 caching requirement).
 *
 * Sleeper is explicit that `/players/nfl` — around 14MB in the 2026 payload —
 * is not meant to be called on every lookup and should be stored locally and
 * refreshed at most once per day. This module owns that TTL so
 * `sources/sleeper.ts` can stay a thin client.
 *
 * The cache lives in IndexedDB on the user's device (FR-64). Nothing is stored
 * server-side and nothing is shared between visitors.
 */

import type { Player, PlayerUniverse } from '../core/types.ts';
import { fetchPlayerUniverse } from '../sources/sleeper.ts';
import { idbGet, idbMeta, idbSet, idbDelete } from './storage.ts';

const KEY = 'players_nfl';
const TTL_MS = 24 * 60 * 60 * 1000;

export interface PlayerCacheStatus {
  loaded: boolean;
  count: number;
  /** Epoch ms of the last successful refresh, or null if never. */
  stored_at: number | null;
  age_hours: number | null;
  stale: boolean;
  source: 'cache' | 'network' | 'none';
}

/**
 * Load the player universe, refreshing only when the cache is missing, older
 * than 24h, or `force` is set (the `--refresh-players` equivalent).
 *
 * IndexedDB cannot store a `Map`, so the universe is serialised as an array of
 * entries. That is deliberate rather than incidental: structured clone *can*
 * store Maps, but doing so ties the on-disk format to a JS type and makes the
 * cache unreadable to anything else. An array of plain objects survives a
 * schema change.
 */
export async function loadPlayerUniverse(
  opts: { force?: boolean; signal?: AbortSignal; onProgress?: (stage: string) => void } = {},
): Promise<{ universe: PlayerUniverse; status: PlayerCacheStatus }> {
  const meta = await idbMeta(KEY);

  if (!opts.force && meta) {
    const age = Date.now() - meta.stored_at;
    if (age < TTL_MS) {
      opts.onProgress?.('Reading cached player list…');
      const cached = await idbGet<Player[]>(KEY);
      if (cached && cached.length > 0) {
        return {
          universe: new Map(cached.map((p) => [p.player_id_sleeper, p])),
          status: {
            loaded: true,
            count: cached.length,
            stored_at: meta.stored_at,
            age_hours: Math.round((age / 3_600_000) * 10) / 10,
            stale: false,
            source: 'cache',
          },
        };
      }
    }
  }

  opts.onProgress?.('Downloading the NFL player list (about 14MB, once a day)…');

  try {
    const universe = await fetchPlayerUniverse(opts.signal);
    await idbSet(KEY, [...universe.values()], TTL_MS);
    return {
      universe,
      status: {
        loaded: true,
        count: universe.size,
        stored_at: Date.now(),
        age_hours: 0,
        stale: false,
        source: 'network',
      },
    };
  } catch (err) {
    // NFR-4: a stale cache beats no board. If the network is down mid-draft,
    // an expired player map is entirely usable — players do not change names.
    const stale = await idbGet<Player[]>(KEY);
    if (stale && stale.length > 0 && meta) {
      return {
        universe: new Map(stale.map((p) => [p.player_id_sleeper, p])),
        status: {
          loaded: true,
          count: stale.length,
          stored_at: meta.stored_at,
          age_hours: Math.round(((Date.now() - meta.stored_at) / 3_600_000) * 10) / 10,
          stale: true,
          source: 'cache',
        },
      };
    }
    throw err;
  }
}

export async function playerCacheStatus(): Promise<PlayerCacheStatus> {
  const meta = await idbMeta(KEY);
  if (!meta) {
    return { loaded: false, count: 0, stored_at: null, age_hours: null, stale: true, source: 'none' };
  }
  const cached = await idbGet<Player[]>(KEY);
  const age = Date.now() - meta.stored_at;
  return {
    loaded: Boolean(cached?.length),
    count: cached?.length ?? 0,
    stored_at: meta.stored_at,
    age_hours: Math.round((age / 3_600_000) * 10) / 10,
    stale: age >= TTL_MS,
    source: cached?.length ? 'cache' : 'none',
  };
}

export const clearPlayerCache = (): Promise<void> => idbDelete(KEY);
